/**
 * WebRTCPlugin
 */
namespace SmartVideo {
  export class WebRTCPlugin {
    browser: string;
    isSmartVideo: boolean;
    CallStateEnum: any;
    SDPStateEnum: any;
    pluginIceServer: any;
    resolutionMap: any;
    webRTCServer: string;
    useCallbackForOutEvents: boolean;
    outEventCallbackObject: any;
    outEventCallbackMethod: string;
    messages: VidyoClientMessages;
    configurationRequest: any;
    sendShareResolution: string;
    started: boolean;
    callState: string;
    sdpState: string;
    session: string;
    guestName: string;
    maxSubscriptions: number;
    currentSubscriptions: number;
    callId: number;
    peerConn: RTCPeerConnection;
    additionalPeerConn: RTCPeerConnection[];
    additionalPeerConnOffers: any[];
    localStream: MediaStream;
    remoteStream: any[];
    audioSources: any[];
    videoSources: any[];
    conferenceName: string;
    participants: any[];
    participantUris: any[];
    activeTalkers: Array<any>;
    volume: number;
    audioOutMuted: boolean;
    videoMuted: boolean;
    shareList: {
      remoteAppName: any[];
      newApp: number;
      remoteAppUri: string[];
      numApp: any;
      currApp: any;
      eventUri: any;
    };
    shareApp: number;
    remoteSharePeerConn: RTCPeerConnection;
    remoteSharePeerOffer: any;
    remoteShareStream: MediaStream;
    remoteShareStreamIndex: number;
    pendingAttaches: any[];
    numDesktops: number;
    currentDesktopId: number;
    localSharePeerConn: RTCPeerConnection;
    localShareStream: MediaStream;
    localShareOffer: any;
    serverMutedAudio: boolean;
    serverMutedVideo: boolean;
    recording: boolean;
    webcast: boolean;
    watchInterval: number;
    windowSizes: any[];
    numPreferred: number;
    activeVideoStreams: number;
    pendingRequestId: number;
    localStats: any;
    showStats: boolean;
    lectureModeListener: boolean;
    lectureModeStarted: boolean;
    lectureModePresenterUri: string;
    webRTCAdapter: WebRTCAdapter;
    peerConnIsNegotiating: boolean;

    constructor(
      browser: string,
      messages: VidyoClientMessages,
      webRTCServer: string,
      webRTCAdapter: WebRTCAdapter
    ) {
      this.browser = browser;
      this.isSmartVideo = browser === 'SmartVideoAgent';
      this.CallStateEnum = {
        IDLE: 'IDLE',
        CALLING: 'CALLING',
        CONNECTING: 'CONNECTING',
        CONNECTED: 'CONNECTED',
        VIDEO_CONNECTED: 'VIDEO_CONNECTED',
        VIDEO_STARTED: 'VIDEO_STARTED',
      };
      this.SDPStateEnum = {
        IDLE: 'IDLE',
        COLLECTING: 'COLLECTING',
        COLLECTED: 'COLLECTED',
      };
      this.pluginIceServer = {
        iceServers: [],
      };
      this.resolutionMap = {
        '180p': {
          w: 320,
          h: 180,
          br: '256',
        },
        '240p': {
          w: 426,
          h: 240,
          br: '384',
        },
        '270p': {
          w: 480,
          h: 270,
          br: '448',
        },
        '360p': {
          w: 640,
          h: 360,
          br: '512',
        },
        '480p': {
          w: 854,
          h: 480,
          br: '768',
        },
        '540p': {
          w: 960,
          h: 540,
          br: '1024',
        },
        '720p': {
          w: 1280,
          h: 720,
          br: '1536',
        },
        '1080p': {
          w: 1920,
          h: 1080,
          br: '2048',
        },
      };
      this.webRTCServer =
        webRTCServer === undefined
          ? ''
          : window.location.protocol + '//' + webRTCServer;
      this.webRTCAdapter = webRTCAdapter;
      this.useCallbackForOutEvents = null;
      this.outEventCallbackObject = null;
      this.outEventCallbackMethod = null;
      this.messages = messages;
      this.configurationRequest = messages.requestGetConfiguration();
      this.configurationRequest.videoPreferences = '';
      this.sendShareResolution = '';
      this.started = false;
      this.callState = this.CallStateEnum.IDLE;
      this.sdpState = this.SDPStateEnum.IDLE;
      this.session = null;
      this.guestName = '';
      this.maxSubscriptions = 0;
      this.currentSubscriptions = 0;
      this.callId = -1;
      this.peerConn = null;
      this.additionalPeerConn = [];
      this.additionalPeerConnOffers = [];
      this.localStream = null;
      this.remoteStream = [];
      this.audioSources = null;
      this.videoSources = null;
      this.conferenceName = '';
      this.participants = [];
      this.participantUris = [];
      this.activeTalkers = [];
      this.volume = 0;
      this.audioOutMuted = false;
      this.videoMuted = false;
      this.shareList = messages.requestWindowShares();
      this.shareApp = 0;
      this.remoteSharePeerConn = null;
      this.remoteSharePeerOffer = null;
      this.remoteShareStream = null;
      this.remoteShareStreamIndex = -1;
      this.pendingAttaches = [];
      this.numDesktops = 0;
      this.currentDesktopId = 1;
      this.localSharePeerConn = null;
      this.localShareStream = null;
      this.localShareOffer = null;
      this.serverMutedAudio = false;
      this.serverMutedVideo = false;
      this.recording = false;
      this.webcast = false;
      this.watchInterval = null;
      this.windowSizes = [];
      this.numPreferred = 0;
      this.activeVideoStreams = 0;
      this.pendingRequestId = -1;
      this.localStats = {};
      this.showStats = false;
      this.lectureModeListener = false;
      this.lectureModeStarted = false;
      this.lectureModePresenterUri = '';
      window.addEventListener('message', this.shareListener.bind(this), false);
    }

    empty() {}

    log(level: string, ts: string, str: string) {
      console[level]('' + ts + ' [WebRTCPlugin] ' + str);
    }

    logInfo(str: string) {
      const ts = new Date().toLocaleTimeString();
      this.log('info', ts, str);
    }

    logError(str: string) {
      const ts = new Date().toLocaleTimeString();
      this.log('error', ts, str);
    }

    logDebug(str: string) {
      const ts = new Date().toLocaleTimeString();
      this.log('debug', ts, str);
    }

    isBundleSupported() {
      switch (this.browser) {
        case 'Opera':
          return true;
        case 'Chrome':
          return true;
        case 'SmartVideoAgent':
          return true;
        case 'Firefox':
          return false;
        case 'Edge':
          return true;
        default:
          break;
      }
    }

    isStarted() {
      return this.started;
    }

    ready(callback: () => void) {
      callback();
    }

    periodicExtensionCheck() {
      // Do this in VidyoDetection
    }

    shareListener(event) {
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data.type === 'VidyoRequestId') {
        this.logInfo('VidyoRequestId - ' + event.data.requestId);
        this.pendingRequestId = event.data.requestId;
      }
      if (event.data.type === 'VidyoOutEventSourceId') {
        this.pendingRequestId = -1;
        if (event.data.sourceId === '') {
          this.currentDesktopId += 1;
          const k = this.messages.event('VidyoOutEventCancelShare');
          this.outEventCallbackObject.dispatchOutEvent(k);
          return;
        }
        let width = 1280;
        let height = 720;
        if (this.resolutionMap.hasOwnProperty(this.sendShareResolution)) {
          width = this.resolutionMap[this.sendShareResolution].w;
          height = this.resolutionMap[this.sendShareResolution].h;
        }
        const constraints = {
          audio: false,
          video: {
            mandatory: {
              chromeMediaSource: 'desktop',
              chromeMediaSourceId: event.data.sourceId,
              maxWidth: width,
              maxHeight: height,
              maxFrameRate: 5,
            },
          },
          optional: [],
        };
        if (this.isBundleSupported()) {
          constraints.optional[0] = { googTemporalLayeredScreencast: true };
        }
        this.startLocalShare(constraints);
      }
    }

    start() {
      if (this.started) {
        this.logInfo('WebRTCClient already started!');
        return true;
      }
      this.started = true;
      this.logDebug('Plugin.start, collecting sources');
      this.webRTCAdapter.getWebRTCSources(this.webRTCGetSources.bind(this));
      return true;
    }

    startLocalShare(constraints: MediaStreamConstraints) {
      const self = this;
      this.logInfo(
        'Starting Local Screen Share with source **' +
          JSON.stringify(constraints) +
          '** ...'
      );
      this.localSharePeerConn = new RTCPeerConnection(this.pluginIceServer);
      this.localSharePeerConn.onicecandidate = function (evt) {
        if (evt.candidate === null) {
          self.logInfo('LocalSharePeerConn: Candidate collection complete');
        } else {
          self.sendCandidate(0, evt.candidate);
        }
      };
      this.localSharePeerConn.oniceconnectionstatechange = function (state) {
        self.logInfo(
          'share: oniceconnectionstatechange called ' +
            state.target.iceConnectionState
        );
        self.postLogToServer(
          'share: oniceconnectionstatechange called ' +
            state.target.iceConnectionState
        );
      };
      this.localSharePeerConn.onsignalingstatechange = function (state) {
        self.logInfo(
          'share: onsignalingstatechange called - ' +
            (state.target ? state.target.signalingState : state)
        );
        self.postLogToServer(
          'share: onsignalingstatechange called - ' +
            (state.target ? state.target.signalingState : state)
        );
      };
      const triggerOffer = function () {
        self.logDebug('share: onnegotiationneeded called');
        const offerConstraints = {
          offerToReceiveAudio: false,
          offerToReceiveVideo: false,
        };
        self.localSharePeerConn.createOffer(
          function (offer: RTCSessionDescriptionInit) {
            self.logInfo('Share: createoffer success ' + offer.sdp);
            self.logInfo('Share: Session: ' + self.session);
            const params = <any>{};
            params.session = self.session;
            params.callId = self.callId;
            params.sdp = offer;
            self.localShareOffer = offer;
            self.postMessageToServer(
              '/startlocalshare',
              params,
              true,
              function (s: any, e: any) {
                if (s) {
                  if (self.localShareOffer !== null) {
                    self.localShareOffer = null;
                    self.localSharePeerConn.setLocalDescription(
                      offer,
                      function () {
                        self.logInfo('Share: setLocalDescription success');
                      },
                      function (err) {
                        self.logError('Share: SetLocalDescription error');
                      }
                    );
                  } else {
                    self.logInfo('startLocalShare - answer came first');
                  }
                } else {
                  self.logError('Share: SetLocalDescription send error');
                }
              }
            );
          },
          function (err) {
            self.logError('Share: CreateOffer error');
          },
          offerConstraints
        );
      };
      this.localSharePeerConn.onnegotiationneeded = triggerOffer;
      this.localSharePeerConn.onaddstream = function (evt) {
        self.logInfo('share: onaddstream called - Should not happen');
      };
      this.localSharePeerConn.onremovestream = function (evt) {
        self.logInfo('share: onremovestream called - Should not happen');
      };
      navigator.mediaDevices
        .getUserMedia(constraints)
        .then(function (shareStream) {
          self.logInfo('Got user media for screen share');
          self.logInfo(
            'shareStream active status is: ' +
              shareStream.active +
              ', has tracks: ' +
              shareStream.getTracks().length +
              ', Id: ' +
              shareStream.id
          );
          self.localShareStream = shareStream;
          shareStream.oninactive = function () {
            self.logInfo('share stream inactive, stopping sharing');
            self.currentDesktopId += 1;
            self.stopLocalShare();
          };
          self.localSharePeerConn.addStream(shareStream);
          const lbl_sharetoggle = <HTMLElement>(
            document.getElementById('lbl_share_toggle')
          );
          lbl_sharetoggle.innerText = 'Sharing ON';
        })
        .catch(function (err) {
          self.logError('Error in Screen Share GUM ' + typeof err);
          console.dir(err);
          self.currentDesktopId += 1;
        });
    }

    stop() {
      this.logDebug('WebRTCClient Stop called');
      this.started = false;
      return true;
    }

    stopLocalShare() {
      const self = this;
      this.logDebug('stopLocalShare called...');
      if (this.pendingRequestId !== -1) {
        window.postMessage(
          {
            type: 'VidyoCancelRequest',
            requestId: this.pendingRequestId,
          },
          '*'
        );
        this.pendingRequestId = -1;
      }
      if (this.localShareStream !== null) {
        this.logInfo('Stopping local share media stream');
        this.webRTCAdapter.stopMediaStream(this.localShareStream);
        this.localShareStream = null;
      }
      if (this.localSharePeerConn !== null) {
        this.localSharePeerConn.close();
        this.localSharePeerConn = null;
        this.localShareOffer = null;
        const params = <any>{};
        params.session = this.session;
        params.callId = this.callId;
        this.postMessageToServer('/stoplocalshare', params, true, this.empty);
      }
    }

    addSubscription() {
      const self = this;
      this.currentSubscriptions += 1;
      if (this.isBundleSupported() || this.currentSubscriptions === 1) {
        this.sendAddStream(
          this.currentSubscriptions,
          {
            sdp: '',
          },
          this.empty
        );
        return;
      }
      const index = this.currentSubscriptions - 1;
      this.additionalPeerConn[index] = new RTCPeerConnection(
        this.pluginIceServer
      );
      this.additionalPeerConn[index].onicecandidate = function (evt) {
        if (evt.candidate === null) {
          self.logInfo('PeerConn' + index + ': Candidate collection complete');
        } else {
          self.sendCandidate(index + 1, evt.candidate);
        }
      };
      this.additionalPeerConn[index].oniceconnectionstatechange = function (
        state
      ) {
        self.logInfo(
          'PeerConn' +
            index +
            ': oniceconnectionstatechange called ' +
            state.target.iceConnectionState
        );
        self.postLogToServer(
          'PeerConn' +
            index +
            ': oniceconnectionstatechange called ' +
            state.target.iceConnectionState
        );
      };
      this.additionalPeerConn[index].onsignalingstatechange = function (state) {
        self.logInfo(
          'PeerConn' +
            index +
            ': onsignalingstatechange called - ' +
            (state.target ? state.target.signalingState : state)
        );
        self.postLogToServer(
          'PeerConn' +
            index +
            ': onsignalingstatechange called - ' +
            (state.target ? state.target.signalingState : state)
        );
      };
      this.additionalPeerConn[index].onaddstream = function (evt) {
        self.logInfo('PeerConn' + index + ': onaddstream called');
        self.remoteStream[index] = evt.stream;
        self.attachRemoteStream('', index + 1, null);
        if (self.remoteStream[index].getAudioTracks()[0]) {
          self.remoteStream[
            index
          ].getAudioTracks()[0].enabled = !self.audioOutMuted;
        }
      };
      this.additionalPeerConn[index].onremovestream = function (evt) {
        self.logInfo('PeerConn' + index + ': onremovestream called');
      };
      const offerConstraints = {
        offerToReceiveAudio: true,
        offerToReceiveVideo: true,
      };
      this.additionalPeerConn[index].createOffer(
        function (offer) {
          self.logInfo(
            'PeerConn' + index + ': createOffer success ' + offer.sdp
          );
          self.additionalPeerConnOffers[index] = offer;
          self.sendAddStream(index + 1, offer, function (s: any, e: any) {
            if (s) {
              if (self.additionalPeerConnOffers[index] !== null) {
                self.additionalPeerConnOffers[index] = null;
                self.additionalPeerConn[index].setLocalDescription(
                  offer,
                  function () {
                    self.logDebug(
                      'PeerConn' + index + ': setlocaldescription success '
                    );
                  },
                  function (err) {
                    self.logError(
                      'PeerConn' + index + ': SetLocalDescription error'
                    );
                  }
                );
              } else {
                self.logDebug('PeerConn' + index + ': answer came first');
              }
            } else {
              self.logError(
                'PeerConn' + index + ': SetLocalDescription send error'
              );
            }
          });
        },
        function (err) {
          self.logError('PeerConn' + index + ': CreateOffer error');
        },
        offerConstraints
      );
    }

    addRemoteStream(label: string, id: number, sdp: RTCSessionDescriptionInit) {
      const self = this;
      console.log('Received AddStream event for: ' + label);
      if (this.isBundleSupported() || id === 1) {
        this.attachRemoteStream(label, id, null);
        return;
      }
      id -= 1;
      if (this.additionalPeerConn[id] === null) {
        this.logInfo('PeerConn' + id + ': PeerConnection null, not adding');
        return;
      }
      const setAdditionalPeerConnRemoteDescription = function () {
        self.additionalPeerConn[id].setRemoteDescription(
          new RTCSessionDescription(sdp),
          function () {
            self.logInfo(
              `PeerConn${id}: Remote SDP set successfully with type ${sdp.type} sdp - ${sdp.sdp}`
            );
          },
          function (e) {
            self.logError(`PeerConn${id}: Remote SDP set failed`);
            console.dir(e);
          }
        );
      };
      if (this.additionalPeerConnOffers[id] !== null) {
        const o = this.additionalPeerConnOffers[id];
        this.additionalPeerConnOffers[id] = null;
        this.logInfo('PeerConn' + id + ': setting offer first');
        this.additionalPeerConn[id].setLocalDescription(
          o,
          function () {
            self.logDebug('PeerConn' + id + ': setlocaldescription success ');
            setAdditionalPeerConnRemoteDescription();
          },
          function (err) {
            self.logError('PeerConn' + id + ': SetLocalDescription error');
          }
        );
      } else {
        setAdditionalPeerConnRemoteDescription();
      }
    }

    addPendingAttach(elementId: string, stream: MediaStream) {
      const obj = <any>{};
      obj.elemId = elementId;
      obj.stream = stream;
      this.pendingAttaches.push(obj);
    }

    attachOrQueueUp(element: string, remoteStream: MediaStream) {
      const remoteView = <HTMLVideoElement>document.getElementById(element);
      if (this.callState === this.CallStateEnum.VIDEO_STARTED) {
        this.logInfo('Attaching Stream to ' + element);
        this.webRTCAdapter.attachMediaStream(remoteView, remoteStream);
        this.handleActiveTalkers(this.activeTalkers.slice());
      } else {
        this.logInfo('Pending attach stream to ' + element);
        this.addPendingAttach(element, remoteStream);
      }
    }

    attachRemoteStream(label: string, id: number, elementId: string) {
      if (this.isBundleSupported()) {
        const remoteStreams = this.peerConn.getRemoteStreams();
        for (let i = 0; i < remoteStreams.length; i++) {
          const remoteStream = remoteStreams[i];
          if (remoteStream && remoteStream.id === label) {
            const elem = elementId || 'remoteVideo' + i;
            this.remoteStream[i] = remoteStream;
            if (this.remoteStream[i].getAudioTracks()[0]) {
              this.remoteStream[i].getAudioTracks()[0].enabled = !this
                .audioOutMuted;
            }
            this.attachOrQueueUp(elem, this.remoteStream[i]);
            return;
          }
        }
        this.logError('Attach Remote Stream label not found - ' + label);
        return;
      }
      id -= 1;
      this.logInfo('Attach Remote Stream using id - ' + id);
      let streamIds = 'Current stream Ids: ';
      this.logInfo('Number of remote streams: ' + this.remoteStream.length);
      this.remoteStream.forEach((stream) => {
        if (stream) {
          streamIds = streamIds + stream.id + ', ';
        } else {
          streamIds = streamIds + 'No stream, ';
        }
      });
      this.logInfo('Current Ids: ' + streamIds);
      const elem1 = elementId || 'remoteVideo' + id;
      this.attachOrQueueUp(elem1, this.remoteStream[id]);
    }

    clearStats() {
      let elem: HTMLElement;
      for (let i = 0; i < this.maxSubscriptions; i++) {
        elem = document.getElementById('participantStats' + i);
        elem.style.display = 'none';
        elem.innerHTML = '';
      }
      document.getElementById('selfViewName').innerHTML = 'Self View';
      elem = document.getElementById('shareStats');
      elem.innerHTML = '';
      elem.style.display = 'none';
    }

    displayStats() {
      let elem = document.getElementById('participantStats0');
      if (!elem) {
        return;
      }
      if (this.showStats === false) {
        return;
      }
      let name = '';
      let statRequest = <any>{};
      for (let i = 0; i < this.activeVideoStreams; i++) {
        statRequest.uri = this.activeTalkers[i].url;
        this.getStatsForParticipant(statRequest);
        elem = document.getElementById('participantStats' + i);
        elem.innerHTML = this.getReceiveStatsForDisplay(statRequest);
        elem.style.display = 'inline-block';
      }
      if (this.shareApp > 0) {
        statRequest = this.localStats.remoteShare;
        elem = document.getElementById('shareStats');
        elem.innerHTML = this.getReceiveStatsForDisplay(statRequest);
        elem.style.display = 'inline-block';
      }
      const localStats = this.localStats.local;
      name =
        'Self View <br> ' +
        localStats.videoResolution.width +
        'x' +
        localStats.videoResolution.height +
        ' ';
      name += localStats.videoKBitsPerSecSend + 'kbps ';
      name += localStats.encodeFrameRate + 'fps ';
      name += localStats.numFirs + '/' + localStats.numNacks;
      if (localStats.noStats) {
        name = 'Self View <br> ...';
      }
      document.getElementById('selfViewName').innerHTML = name;
    }

    doPendingAttaches() {
      this.logDebug(
        'Doing pending attaches count=' + this.pendingAttaches.length
      );
      for (let i = 0; i < this.pendingAttaches.length; i++) {
        const obj = this.pendingAttaches[i];
        const elemId = obj.elemId;
        const stream = obj.stream;
        const elem = <HTMLVideoElement>document.getElementById(elemId);
        this.webRTCAdapter.attachMediaStream(elem, stream);
      }
      if (this.shareApp !== 0) {
        const shareName = document.getElementById('shareName');
        shareName.innerHTML = this.shareList.remoteAppName[
          this.shareList.newApp - 1
        ];
      }
      this.pendingAttaches.length = 0;
      this.handleActiveTalkers(this.activeTalkers.slice());
      this.watchForViewChange();
      const disableFunc = function (e) {
        e.preventDefault();
        return false;
      };
      const videoElements = document.getElementsByTagName('video');
      for (let k = 0; k < videoElements.length; k++) {
        videoElements[k].addEventListener('contextmenu', disableFunc);
      }
    }

    removeSubscription() {
      this.currentSubscriptions -= 1;
      const index = this.currentSubscriptions;
      if (!this.isBundleSupported()) {
        if (this.additionalPeerConn[index]) {
          this.additionalPeerConn[index].close();
          this.additionalPeerConn[index] = null;
          this.additionalPeerConnOffers[index] = null;
        }
      }
      this.sendRemoveStream(index + 1);
      this.removeRemoteStream(index);
    }

    removeRemoteStream(id: number) {
      const remoteStream = this.remoteStream[id];
      if (remoteStream) {
        this.logInfo('Detaching stream from ' + id);
        const remoteView = <HTMLVideoElement>(
          document.getElementById('remoteVideo' + id)
        );
        this.remoteStream[id] = null;
        this.webRTCAdapter.detachMediaStream(
          remoteView,
          remoteStream,
          false,
          false
        );
        const docElement = document.getElementById('participant' + id);
        if (docElement) {
          docElement.innerHTML = '';
        }
        return;
      }
    }

    changeCallState(newState: string) {
      this.logInfo(
        'Changing CallState from ' + this.callState + ' to ' + newState
      );
      this.callState = newState;
    }

    collectIceAndSendSdp() {
      const self = this;
      if (this.sdpState !== this.SDPStateEnum.IDLE) {
        this.logDebug(
          'Ignore collect SDP request since in state - ' + this.sdpState
        );
        return;
      }
      this.logDebug('Changing SDP State to COLLECTING');
      this.sdpState = this.SDPStateEnum.COLLECTING;
      const rtcConfig = this.pluginIceServer;
      if (
        this.browser.toLowerCase() === 'chrome' ||
        this.browser.toLowerCase() === 'opera'
      ) {
        rtcConfig.sdpSemantics = 'plan-b';
        this.peerConn = new RTCPeerConnection(rtcConfig);
      } else {
        this.peerConn = new RTCPeerConnection(this.pluginIceServer);
      }
      this.peerConn.onicecandidate = function (evt) {
        if (evt.candidate === null) {
          self.logDebug('Main: Candidate collection complete');
        } else {
          self.sendCandidate(1, evt.candidate);
        }
      };
      this.peerConn.oniceconnectionstatechange = function (state) {
        self.logInfo(
          'oniceconnectionstatechange called - ' +
            state.target.iceConnectionState
        );
        self.postLogToServer(
          'main: oniceconnectionstatechange called - ' +
            state.target.iceConnectionState
        );
      };
      this.peerConn.onsignalingstatechange = function (state) {
        self.logInfo(
          'onsignalingstatechange called - ' +
            (state.target ? state.target.signalingState : state)
        );
        self.postLogToServer(
          'main: onsignalingstatechange called - ' +
            (state.target ? state.target.signalingState : state)
        );
        self.peerConnIsNegotiating = self.peerConn.signalingState !== 'stable';
      };
      // Chrome's onnegotiatied is busted and fires multiple times, creating havoc
      // Add guard to prevent nested negotiations
      this.peerConnIsNegotiating = false;
      const triggerLocalOffer = function () {
        if (this.peerConnIsNegotiating) {
          console.log('Killing nested onnegotiation call');
          return;
        }
        this.peerConnIsNegotiating = true;
        const offerConstraints = {
          offerToReceiveAudio: true,
          offerToReceiveVideo: true,
        };
        self.logDebug('onnegotiationneeded called');
        self.peerConn.createOffer(
          function (offer: any) {
            self.localDescCreated(offer);
          },
          function (err) {
            self.logError('CreateOffer error');
            console.dir(err);
          },
          offerConstraints
        );
      };
      this.peerConn.onnegotiationneeded = triggerLocalOffer;
      this.peerConn.onaddstream = function (evt) {
        self.logInfo('onaddstream called - ' + evt.stream.id);
        if (!self.isBundleSupported()) {
          self.remoteStream[0] = evt.stream;
        }
      };
      this.peerConn.onremovestream = function (evt) {
        self.logError('onremovestream called - ' + evt.stream.id);
      };
      let videoId = '';
      let videoLabel = '';
      let audioId = '';

      if (this.configurationRequest.numberCameras > 0) {
        videoId = this.videoSources[self.configurationRequest.currentCamera].id;
        videoLabel = this.videoSources[self.configurationRequest.currentCamera]
          .label;
        this.logInfo(
          'Current camera ' +
            this.configurationRequest.currentCamera +
            ' Id: ' +
            videoId +
            ' Label: ' +
            videoLabel
        );
      }
      if (this.configurationRequest.numberMicrophones > 0) {
        audioId = this.audioSources[this.configurationRequest.currentMicrophone]
          .id;
        this.logInfo(
          'Current Mic ' +
            this.configurationRequest.currentMicrophone +
            ' Id: ' +
            audioId +
            ' Label: ' +
            this.audioSources[self.configurationRequest.currentMicrophone].label
        );
      }

      this.logInfo(
        'Video Preference: ' + this.configurationRequest.videoPreferences
      );
      const optionalConstraintObj = [];
      const mandatoryConstraintObj = <any>{};
      const mediaOptions = <any>{
        audio: {
          optional: [
            {
              sourceId: audioId,
            },
          ],
        },
      };
      console.log('VideoId: ' + videoId);
      if (videoId !== '') {
        const w = this.resolutionMap[this.configurationRequest.videoPreferences]
          .w;
        const h = this.resolutionMap[this.configurationRequest.videoPreferences]
          .h;
        switch (this.configurationRequest.videoPreferences) {
          case '1080p':
          case '720p':
          case '540p':
          case '480p':
          case '360p':
          case '270p':
          case '240p':
          case '180p':
            optionalConstraintObj.push({
              height: h,
            });
            optionalConstraintObj.push({
              width: w,
            });
            mandatoryConstraintObj.maxHeight = h;
            mandatoryConstraintObj.maxWidth = w;
            break;
          default:
            this.logInfo(
              'UNKNOWN VIDEO PREFERENCE - ' +
                this.configurationRequest.videoPreferences
            );
            break;
        }
        optionalConstraintObj.push({
          sourceId: videoId,
        });
        mediaOptions.video = {
          mandatory: mandatoryConstraintObj,
          optional: optionalConstraintObj,
        };
      }
      if (audioId === '0') {
        mediaOptions.audio = true;
      }
      if (videoId === '0') {
        delete mediaOptions.video.optional.sourceId;
      }
      if (videoId === '') {
        mediaOptions.video = {
          width: 320,
        };
      }
      if (audioId === '') {
        mediaOptions.audio = false;
      }
      console.log(mediaOptions);
      try {
        navigator.mediaDevices
          .getUserMedia(mediaOptions)
          .then(function (stream) {
            self.localStream = stream;
            self.addPendingAttach('localVideo', self.localStream);
            self.peerConn.addStream(stream);
          })
          .catch(function (err) {
            self.logError('GetUserMedia error');
            self.postLogToServer('GetUserMedia error: ' + JSON.stringify(err));
            console.dir(err);
            self.handleConnectResponse({
              error: 'Camera/Microphone access failed',
            });
          });
      } catch (error) {
        console.dir(error);
      }
    }

    getConfiguration(request: { [x: string]: any }) {
      for (const item in this.configurationRequest) {
        if (this.configurationRequest.hasOwnProperty(item)) {
          request[item] = this.configurationRequest[item];
        }
      }
    }

    getLocalEncodeResolution(request: { width: any; height: any }) {
      request.width = this.localStats.local.videoResolution.width;
      request.height = this.localStats.local.videoResolution.height;
    }

    getLocalFrameRates(request: {
      captureFrameRate: any;
      encodeFrameRate: any;
      sendFrameRate: any;
    }) {
      request.captureFrameRate = this.localStats.local.captureFrameRate;
      request.encodeFrameRate = this.localStats.local.encodeFrameRate;
      request.sendFrameRate = this.localStats.local.encodeFrameRate;
    }

    getLocalMediaInfo(request: {
      numIFrames: number;
      numFirs: any;
      numNacks: any;
      mediaRTT: any;
    }) {
      request.numIFrames = 0;
      request.numFirs = this.localStats.local.numFirs;
      request.numNacks = this.localStats.local.numNacks;
      request.mediaRTT = this.localStats.local.mediaRTT;
    }

    getNumberOfLocalShares() {
      // Code should be reworked to be compatible with VidyoDetection
      if (window.location.protocol !== 'https:') {
        return 0;
      }
      if (this.isBundleSupported()) {
        if (document.getElementById('vidyowebrtcscreenshare_is_installed')) {
          this.logInfo('vidyowebrtcscreenshare_is_installed');
          return 1;
        }
        this.periodicExtensionCheck();
        this.logInfo('vidyowebrtcscreenshare_is_installed NOT INSTALLED');
        return 0;
      }
      return 1;
    }

    getReceiveStatsForDisplay(statRequest: {
      videoResolution: { width: string; height: string };
      videoKBitsPerSecRecv: string;
      videoDisplayedFrameRate: string;
      firs: string;
      nacks: string;
      packetsLost: string;
      noStats: any;
    }) {
      let name =
        statRequest.videoResolution.width +
        'x' +
        statRequest.videoResolution.height +
        ' ';
      name += statRequest.videoKBitsPerSecRecv + 'kbps ';
      name += statRequest.videoDisplayedFrameRate + 'fps ';
      name +=
        statRequest.firs +
        '/' +
        statRequest.nacks +
        '/' +
        statRequest.packetsLost;
      if (statRequest.noStats) {
        name = '...';
      }
      return name;
    }

    getStreamIndex(uri: any) {
      for (let i = 0; i < this.activeVideoStreams; ++i) {
        if (this.activeTalkers[i].url === uri) {
          return i;
        }
      }
      return -1;
    }

    getStatsForParticipant(statsRequest: { [x: string]: any; uri: any }) {
      const index = this.getStreamIndex(statsRequest.uri);
      if (index === -1) {
        return;
      }
      if (this.localStats[index]) {
        for (const s in this.localStats[index]) {
          if (this.localStats[index].hasOwnProperty(s)) {
            statsRequest[s] = this.localStats[index][s];
          }
        }
      }
    }

    getStatsForStreamIndex(index: number, statsRequest: { [x: string]: any }) {
      let pc = this.peerConn;
      if (!this.isBundleSupported() && index > 0) {
        pc = this.additionalPeerConn[index];
      }
      if (!this.localStats[index]) {
        this.localStats[index] = {
          videoResolution: {
            height: 0,
            width: 0,
          },
          videoFrameRate: 0,
          videoDecodedFrameRate: 0,
          videoDisplayedFrameRate: 0,
          videoKBitsPerSecRecv: 0,
          firs: 0,
          nacks: 0,
          packetsLost: 0,
          lastBytesReceivedTS: Date.now(),
          noStats: true,
        };
      }
      for (const s in this.localStats[index]) {
        if (this.localStats[index].hasOwnProperty(s)) {
          statsRequest[s] = this.localStats[index][s];
        }
      }
      const self = this;
      const track = this.remoteStream[index].getVideoTracks()[0];
      this.webRTCAdapter.getWebRTCStats(pc, track, function (streamStats) {
        if (!streamStats.hasOwnProperty('bytesReceived')) {
          return;
        }
        if (self.localStats[index].bytesReceived) {
          const timeDiff =
            (Date.now() - self.localStats[index].lastBytesReceivedTS) / 1000;
          const bitDiff =
            ((streamStats.bytesReceived -
              self.localStats[index].bytesReceived) *
              8) /
            1000;
          streamStats.videoKBitsPerSecRecv = Math.floor(bitDiff / timeDiff);
        } else {
          streamStats.videoKBitsPerSecRecv = 0;
          streamStats.noStats = true;
        }
        self.localStats[index] = streamStats;
        self.localStats[index].lastBytesReceivedTS = Date.now();
      });
    }

    getSourceId(val) {
      if (!this.isSmartVideo) {
        if (this.isBundleSupported()) {
          if (this.pendingRequestId === -1) {
            window.postMessage(
              {
                type: 'VidyoRequestGetWindowsAndDesktops',
              },
              '*'
            );
          }
          return;
        }
      }
      const constraints = {
        video: {
          mediaSource: 'window',
          mozMediaSource: 'window',
        },
      };
      this.startLocalShare(constraints);
    }

    getWindowSizes() {
      const windowSizes = [];
      for (let i = 0; i < this.activeVideoStreams; i++) {
        // Firefox doesn't calculate video height/width same as Chrome, so use different method to calculate size
        const remoteView = <HTMLVideoElement>(
          document.getElementById('remoteVideo' + i)
        );
        if (remoteView) {
          const windowsize = <any>{};
          windowsize.width = remoteView.clientWidth;
          windowsize.height = remoteView.clientHeight;
          this.logInfo(
            'getWindowSizes: Video window calculated at width: ' +
              windowsize.width +
              ', height: ' +
              windowsize.height
          );
          if (windowsize.width === 0 || windowsize.height === 0) {
            break;
          }
          let maxWidth = 640;
          let maxHeigth = 360;
          if (
            this.resolutionMap.hasOwnProperty(
              this.configurationRequest.videoPreferences
            )
          ) {
            maxWidth = this.resolutionMap[
              this.configurationRequest.videoPreferences
            ].w;
            maxHeigth = this.resolutionMap[
              this.configurationRequest.videoPreferences
            ].h;
          }
          if (windowsize.width > maxWidth) {
            windowsize.width = maxWidth;
          }
          if (windowsize.height > maxHeigth) {
            windowsize.height = maxHeigth;
          }
          windowSizes.push(windowsize);
        }
      }
      return windowSizes;
    }

    getEvents() {
      const self = this;
      if (this.callState !== this.CallStateEnum.IDLE) {
        const oReq = new XMLHttpRequest();
        oReq.open('post', this.webRTCServer + '/events', true);
        const params = <any>{};
        params['session'] = self.session;
        params['callId'] = self.callId;
        oReq.onload = function () {
          if (oReq.status === 404 || oReq.status === 503) {
            self.logError(
              'getEvents received ' + oReq.status + ' disconnecting call'
            );
            console.log(
              'getEvents received ' + oReq.status + ' disconnecting call'
            );
            self.sendDisconnect(false);
            return;
          }
          self.logDebug('Received Events  - ' + oReq.responseText);
          const evts = JSON.parse(oReq.responseText);
          for (let r = 0; r < evts.length; r++) {
            const res = evts[r];
            if (res.type === 'connectResponse') {
              self.handleConnectResponse(res);
            } else if (res.type === 'callDisconnected') {
              self.sendDisconnect(false);
              console.log('Received callDisconnected from Vidyo server');
            } else if (res.type === 'sdp') {
              self.conferenceName = res.conf;
              self.handleRemoteSdp(res.sdp);
            } else if (res.type === 'participantsChanged') {
              self.handleParticipantsChanged(res);
            } else if (res.type === 'activeTalkersChanged') {
              self.handleActiveTalkers(res.names);
            } else if (res.type === 'streamAdded') {
              self.addRemoteStream(res.label, res.id, res.sdp);
            } else if (res.type === 'shareAdded') {
              self.handleShareAdded(res);
            } else if (res.type === 'shareRemoved') {
              self.handleShareRemoved(res);
            } else if (res.type === 'shareStreamChanged') {
              self.handleShareStreamChange(res.label, res.sdp);
            } else if (res.type === 'startLocalShareResponse') {
              self.handleStartLocalShareResponse(res.sdp);
            } else if (res.type === 'serverMutedAudio') {
              self.handleServerMutedAudio(res.muted, res.unmuteable);
            } else if (res.type === 'serverMutedVideo') {
              self.handleServerMutedVideo(res.muted, res.unmuteable);
            } else if (res.type === 'recordingStatus') {
              self.handleRecordingStatus(res.status);
            } else if (res.type === 'webcastStatus') {
              self.handleWebcastStatus(res.status);
            } else if (res.type === 'groupChatMessage') {
              self.handleGroupChatMessage(res);
            } else if (res.type === 'privateChatMessage') {
              self.handlePrivateChatMessage(res);
            } else if (res.type === 'lectureModeStage') {
              self.handleLectureModeStage(res.stage);
            } else if (res.type === 'lectureHand') {
              self.handleLectureHand();
            } else if (res.type === 'lectureModePresenterChanged') {
              self.handleLectureModePresenterChanged(res.uriOfPresenter);
            } else if (res.type === 'candidateEvent') {
              self.handleCandidate(res);
            } else {
              self.logError('Unhandled event from server - ' + res.type);
            }
          }
          self.getEvents();
        };
        oReq.onerror = function (e) {
          self.logError('getEvents error');
          console.dir(e);
          self.sendDisconnect(false);
          console.log('Ajax error in WebRTCPlugin::getEvents');
        };
        oReq.send(JSON.stringify(params));
      }
    }

    setConfiguration(request: any) {
      for (const item in request) {
        if (request.hasOwnProperty(item)) {
          this.configurationRequest[item] = request[item];
        }
      }
    }

    sendAddStream(streamId: number, sdp: { sdp: any }, callback: any) {
      const params = <any>{};
      params.session = this.session;
      params.callId = this.callId;
      params.streamId = streamId;
      params.sdp = sdp;
      this.logInfo('sendAddStream sdp - ' + sdp.sdp);
      this.postMessageToServer('/addstream', params, true, callback);
    }

    sendRemoveStream(streamId: number) {
      const params = <any>{};
      params.session = this.session;
      params.callId = this.callId;
      params.streamId = streamId;
      this.postMessageToServer('/removestream', params, true, this.empty);
    }

    sendEvent(e: any) {
      const self = this;
      switch (e.type) {
        case 'PrivateInEventVcsoapGuestLink':
          this.sendConnect(e);
          this.numDesktops = this.getNumberOfLocalShares();
          break;
        case 'InEventLeave':
          this.logInfo('InEventLeave - hangup call');
          this.sendDisconnect(true);
          break;
        case 'EventPreview':
          this.updateSelfView(e.previewMode);
          break;
        case 'InEventLoginCancel':
          this.logInfo('InEventLoginCancel - Cancel call');
          this.sendDisconnect(true);
          break;
        case 'InEventMuteAudioIn':
          this.handleMuteAudioIn(e);
          break;
        case 'InEventMuteAudioOut':
          this.handleMuteAudioOut(e);
          break;
        case 'InEventMuteVideo':
          this.handleMuteVideo(e);
          break;
        case 'InEventShare':
          this.getSourceId(e.window);
          break;
        case 'InEventUnshare':
          this.stopLocalShare();
          break;
        case 'InEventLayout':
          this.logDebug('InEventLayout preferred - ' + e.numPreferred);
          this.numPreferred = e.numPreferred;
          this.windowSizes = this.getWindowSizes();
          this.windowSizeChanged(this.windowSizes);
          break;
        case 'InEventGroupChat':
          const params = <any>{};
          params.callId = this.callId;
          params.session = this.session;
          params.message = e.message;
          this.postMessageToServer(
            '/groupchatmessage',
            params,
            true,
            this.empty
          );
          break;
        case 'InEventPrivateChat':
          const param = <any>{};
          param.callId = this.callId;
          param.session = this.session;
          param.uri = e.uri;
          param.message = e.message;
          this.postMessageToServer(
            '/privatechatmessage',
            param,
            true,
            this.empty
          );
          break;
        case 'InEventSetFreezeImage':
          this.handleFreezeImage(e);
          break;
        case 'InEventToggleStats':
          this.showStats = e.show;
          if (this.showStats === false) {
            this.clearStats();
          } else {
            this.displayStats();
          }
          break;
        case 'InEventLectureClearRaiseHand':
          const lectureHandMsg = <any>{};
          lectureHandMsg.callId = this.callId;
          lectureHandMsg.session = this.session;
          lectureHandMsg.raise = e.raise;
          this.postMessageToServer(
            '/lecturehand',
            lectureHandMsg,
            true,
            this.empty
          );
          break;
        default:
          this.logError('sendEvent unhandled - ' + JSON.stringify(e));
          return false;
      }
      return true;
    }

    sendRequest(request) {
      switch (request.type) {
        case 'RequestGetConfiguration':
          this.getConfiguration(request);
          break;
        case 'RequestActiveTalkers':
          let activeTalkerCount = 0;
          for (let index = 0; index < this.activeTalkers.length; index++) {
            const talker = this.activeTalkers[index];
            if (talker.active) {
              activeTalkerCount += 1;
            }
          }
          request.activeTalkers = this.activeTalkers;
          request.numActiveTalkers = activeTalkerCount;
          break;
        case 'RequestSetConfiguration':
          this.setConfiguration(request);
          break;
        case 'RequestGetCallState':
          if (
            this.callState === this.CallStateEnum.VIDEO_CONNECTED ||
            this.callState === this.CallStateEnum.VIDEO_STARTED
          ) {
            request.callState = 'InConference';
          } else {
            request.callState = 'Idle';
          }
          break;
        case 'RequestGetWindowsAndDesktops':
          request.appWindowAppName = [];
          request.appWindowId = [];
          request.appWindowName = [];
          if (this.numDesktops > 0) {
            request.numSystemDesktops = 1;
            request.sysDesktopName[0] = 'Display';
            request.sysDesktopId = [this.currentDesktopId.toString()];
          } else {
            request.numSystemDesktops = 0;
            request.sysDesktopName = [];
            request.sysDesktopId = [];
          }
          break;
        case 'RequestGetCurrentSessionDisplayInfo':
          this.logInfo(
            'RequestGetCurrentSessionDisplayInfo - ' + this.conferenceName
          );
          request.sessionDisplayText = this.conferenceName;
          break;
        case 'RequestGetCurrentUser':
          request.currentUserDisplay = this.guestName;
          break;
        case 'RequestGetEncodeResolution':
          this.getLocalEncodeResolution(request.rect);
          break;
        case 'RequestGetVideoFrameRateInfo':
          this.getLocalFrameRates(request);
          break;
        case 'RequestGetMediaInfo':
          this.getLocalMediaInfo(request);
          break;
        case 'RequestGetNumParticipants':
          request.numParticipants = this.participants.length;
          this.logInfo(
            'RequestGetNumParticipants - ' + request.numParticipants
          );
          break;
        case 'RequestGetParticipantStatisticsAt':
          request.result = true;
          request.uri = this.participantUris[request.index];
          request.name = this.participants[request.index];
          this.getStatsForParticipant(request);
          break;
        case 'RequestGetParticipants':
          request.name = this.participants;
          request.URI = this.participantUris;
          request.numberParticipants = this.participants.length;
          break;
        case 'RequestGetVolumeAudioOut':
          this.logDebug('RequestGetVolumeAudioOut - ' + this.volume);
          request.volume = this.volume;
          break;
        case 'RequestSetVolumeAudioOut':
          this.logDebug('RequestSetVolumeAudioOut - ' + request.volume);
          this.handleSetAudioVolume(request.volume);
          break;
        case 'RequestGetMutedAudioOut':
          this.logDebug('RequestGetMutedAudioOut - false');
          request.isMuted = this.audioOutMuted;
          break;
        case 'RequestGetMutedAudioIn':
          this.logDebug('RequestGetMutedAudioIn - false');
          request.isMuted = !this.localStream.getAudioTracks()[0].enabled;
          break;
        case 'RequestGetMutedVideo':
          this.logDebug('RequestGetMutedVideo - false');
          request.isMuted = this.videoMuted;
          break;
        case 'RequestGetMutedServerAudioIn':
          this.logDebug(
            'RequestGetMutedServerAudioIn - ' + this.serverMutedAudio
          );
          request.isMuted = this.serverMutedAudio;
          break;
        case 'RequestGetMutedServerVideo':
          this.logDebug(
            'RequestGetMutedServerVideo - ' + this.serverMutedVideo
          );
          request.isMuted = this.serverMutedVideo;
          break;
        case 'RequestGetWindowShares':
          request.requestType = 'ListSharingWindows';
          request.remoteAppUri = this.shareList.remoteAppUri;
          request.remoteAppName = this.shareList.remoteAppName;
          request.numApp = this.shareList.numApp;
          request.currApp = this.shareList.currApp;
          request.eventUri = this.shareList.eventUri;
          request.newApp = this.shareList.newApp;
          this.logDebug('RequestGetWindowShares - ' + JSON.stringify(request));
          break;
        case 'RequestSetWindowShares':
          this.logInfo('RequestSetWindowShares - ' + JSON.stringify(request));
          this.shareList.newApp = request.newApp;
          this.sendChangeShare(request.newApp);
          break;
        case 'RequestGetPreviewMode':
          this.logInfo('RequestGetPreviewMode - Attaching video streams');
          if (this.callState === this.CallStateEnum.VIDEO_CONNECTED) {
            this.changeCallState(this.CallStateEnum.VIDEO_STARTED);
            this.doPendingAttaches();
          }
          if (this.activeVideoStreams > 0) {
            this.updateActiveVideoStreams(this.activeVideoStreams);
          }
          break;
        case 'RequestGetConferenceInfo':
          request.recording = this.recording;
          request.webcasting = this.webcast;
          this.logDebug(
            'RequestGetConferenceInfo - ' +
              request.recording +
              ', ' +
              request.webcasting
          );
          break;
        case 'RequestGetLectureModeStatus':
          request.inLectureMode = this.lectureModeStarted;
          request.inPresenterMode =
            this.lectureModeStarted && !this.lectureModeListener;
          request.isTherePresenter = this.lectureModePresenterUri !== '';
          break;
        default:
          this.logError('sendRequest unhandled - ' + JSON.stringify(request));
          return 'ErrorInvalidRequest';
      }
      return 'ErrorOk';
    }

    sendInConference() {
      if (
        this.callState !== this.CallStateEnum.VIDEO_CONNECTED &&
        this.callState !== this.CallStateEnum.VIDEO_STARTED
      ) {
        this.logInfo('InConference Event --> UI');
        this.changeCallState(this.CallStateEnum.VIDEO_CONNECTED);
        const evt = this.messages.event('OutEventCallState');
        evt.callState = 'InConference';
        evt.error = 0;
        evt.activeEid = -1;
        this.outEventCallbackObject.dispatchOutEvent(evt);
        const confActiveEvent = this.messages.event('OutEventConferenceActive');
        this.outEventCallbackObject.dispatchOutEvent(confActiveEvent);
        if (this.callState === this.CallStateEnum.VIDEO_CONNECTED) {
          this.changeCallState(this.CallStateEnum.VIDEO_STARTED);
          this.doPendingAttaches();
        }
      }
    }

    sendConnect(e: {
      portalUri: any;
      roomKey: any;
      pin: string;
      requestId: any;
      guestName: string;
    }) {
      const self = this;
      const oReq = new XMLHttpRequest();
      oReq.open('post', this.webRTCServer + '/connect', true);
      const params = <any>{};
      params.portalUri = e.portalUri;
      params.roomKey = e.roomKey;
      if (e.pin) {
        console.log('sendConnect PIN: ' + e.pin);
        params.pin = e.pin;
      } else {
        params.pin = '';
      }
      params.sessionId = e.requestId;
      params.guestName = e.guestName;
      params.videoPreferences = this.configurationRequest.videoPreferences;
      params.callType = 'webrtc';
      this.guestName = e.guestName;
      if (this.callState === this.CallStateEnum.IDLE) {
        this.changeCallState(this.CallStateEnum.CALLING);
      } else {
        this.logError('Invalid state for ConnectRequest - ' + this.callState);
      }
      oReq.onload = function () {
        if (oReq.status === 404 || oReq.status === 503) {
          self.logError(
            '/connect received ' + oReq.status + ' disconnecting call'
          );
          self.handleConnectResponse({
            error: 'Server unreachable - ' + oReq.status,
          });
          return;
        }
        const response = JSON.parse(oReq.responseText);
        self.logInfo('Response for connect - ' + JSON.stringify(response));
        self.session = response.session;
        self.logInfo('sendConnect: Session: ' + self.session);
        self.callId = response.callId;
        self.configurationRequest.videoPreferences = response.maxResolution;
        self.maxSubscriptions = response.maxSubscriptions;
        self.sendShareResolution = response.maxShareResolution;
        self.remoteShareStreamIndex = response.maxSubscriptions + 1;
        self.pluginIceServer.iceServers.length = 0;
        self.pluginIceServer.iceServers.push({
          urls: 'stun:' + response.stunServer,
        });
        let addr = window.location.hostname;

        /* Check the connect response to see if it supplies a different host to use as
                the WebRTC session manager. In a load balancing situation, the initial WebRTC session manager FQDN
                may ultimately resolve down to a different machine. The host field in the response supplies the
                FQDN of the host we ultimately connected to. */
        if (response.host.length > 0) {
          self.webRTCServer = window.location.protocol + '//' + response.host;
          addr = response.host;
          console.log(
            '/connect json response contains host, resetting WebRTC session manager to: ' +
              response.host
          );
        }

        if (response.turnCreds) {
          const urls = response.turnCreds.urls;
          for (let k = 0; k < urls.length; k++) {
            if (urls[k].indexOf('self_address') !== -1) {
              urls[k] = urls[k].replace('self_address', addr);
            }
          }
          self.pluginIceServer.iceServers.push(response.turnCreds);
        }
        const server =
          self.webRTCServer === '' ? window.location.origin : self.webRTCServer;
        const statsUrl =
          server +
          '/web/stats.html?session=' +
          self.session +
          '&callid=' +
          self.callId;
        self.logInfo('Stats URL - ' + statsUrl);
        if (self.callState === self.CallStateEnum.CALLING) {
          self.changeCallState(self.CallStateEnum.CONNECTING);
          self.getEvents();
        } else {
          self.logError(
            'Invalid state for ConnectResponse - ' + self.callState
          );
        }
      };
      oReq.onerror = function (error) {
        self.logError('Connect Error');
        self.handleConnectResponse({
          error: 'Server unreachable',
        });
      };
      const paramStr = JSON.stringify(params);
      this.logInfo('/connect - ' + paramStr);
      oReq.send(paramStr);
    }

    sendDisconnect(localDisconnect: boolean) {
      this.logInfo(
        'Leaving the conference call state - ' +
          this.callState +
          ' LocalDisconnect: ' +
          localDisconnect
      );
      if (this.callState === this.CallStateEnum.IDLE) {
        return;
      }
      if (this.pendingRequestId !== -1) {
        window.postMessage(
          {
            type: 'VidyoCancelRequest',
            requestId: this.pendingRequestId,
          },
          '*'
        );
        this.pendingRequestId = -1;
      }
      const localVideo = <HTMLVideoElement>(
        document.getElementById('localVideo')
      );
      this.webRTCAdapter.detachMediaStream(
        localVideo,
        this.localStream,
        true,
        true
      );
      let remoteView: HTMLVideoElement,
        elemId: string,
        participantElem: HTMLElement;
      for (let i = 0; i < this.maxSubscriptions; i++) {
        elemId = 'remoteVideo' + i;
        remoteView = document.getElementById(elemId) as HTMLVideoElement;
        this.webRTCAdapter.detachMediaStream(
          remoteView,
          this.remoteStream[i],
          false,
          true
        );
        participantElem = document.getElementById('participant' + i);
        if (participantElem) {
          participantElem.innerHTML = '';
        }
        if (this.additionalPeerConn[i]) {
          this.additionalPeerConn[i].close();
          this.additionalPeerConn[i] = null;
        }
        this.additionalPeerConn.length = 0;
        this.additionalPeerConnOffers.length = 0;
      }
      const shareView = <HTMLVideoElement>(
        document.getElementById('shareVideo0')
      );
      this.webRTCAdapter.detachMediaStream(
        shareView,
        this.remoteShareStream,
        false,
        true
      );
      if (this.remoteSharePeerConn !== null) {
        this.remoteSharePeerConn.close();
        this.remoteSharePeerConn = null;
        this.remoteSharePeerOffer = null;
        this.remoteShareStream = null;
      }
      participantElem = document.getElementById('shareName');
      if (participantElem) {
        participantElem.innerHTML = '';
      }
      if (this.showStats) {
        this.clearStats();
      }
      this.showStats = false;
      this.localStats = {};
      this.stopLocalShare();
      if (this.peerConn !== null) {
        this.peerConn.close();
        this.peerConn = null;
      }
      this.remoteStream.length = 0;
      this.activeTalkers.length = 0;
      this.participants.length = 0;
      this.participantUris.length = 0;
      this.currentSubscriptions = 0;
      this.shareApp = 0;
      this.shareList = this.messages.requestWindowShares();
      this.activeVideoStreams = 0;
      this.lectureModeListener = false;
      this.lectureModeStarted = false;
      this.lectureModePresenterUri = '';
      clearInterval(this.watchInterval);
      this.windowSizes.length = 0;
      this.numPreferred = 0;
      this.changeCallState(this.CallStateEnum.IDLE);
      this.sdpState = this.SDPStateEnum.IDLE;
      this.audioOutMuted = false;
      this.videoMuted = false;
      this.serverMutedAudio = false;
      this.serverMutedVideo = false;
      this.recording = false;
      this.webcast = false;
      if (localDisconnect) {
        const params = <any>{};
        params.session = this.session;
        params.callId = this.callId;
        params.callType = 'webrtc';
        this.postMessageToServer('/disconnect', params, true, this.empty);
      }
      this.handleDisconnectedEvent(localDisconnect);
    }

    sendChangeShareRequest(eventUri: string, sdp: { sdp: any }, callback: any) {
      const params = <any>{};
      params.session = this.session;
      params.callId = this.callId;
      params.eventUri = eventUri;
      params.sdp = sdp;
      this.logInfo('sendChangeShareRequest sdp - ' + sdp.sdp);
      this.postMessageToServer('/changeshare', params, true, callback);
    }

    sendCandidate(streamId: number, candidate: RTCIceCandidate) {
      const params = <any>{};
      params.session = this.session;
      params.callId = this.callId;
      params.streamId = streamId;
      params.candidate = candidate.candidate;
      params.sdpMid = candidate.sdpMid;
      params.sdpMlineIndex = candidate.sdpMLineIndex;
      this.postMessageToServer('/candidate', params, true, this.empty);
    }

    sendChangeShare(newApp: number) {
      const self = this;
      if (newApp === 0 && this.shareApp === 0) {
        return;
      }
      let eventUri = '';
      let remoteAppName = '';
      if (newApp > 0) {
        eventUri = this.shareList.remoteAppUri[newApp - 1];
        remoteAppName = this.shareList.remoteAppName[newApp - 1];
      }
      this.logInfo(
        'Changing the share from ' +
          this.shareApp +
          ' to ' +
          newApp +
          ': Name - ' +
          remoteAppName +
          ' EventURI: ' +
          eventUri
      );
      if (this.isBundleSupported()) {
        this.sendChangeShareRequest(
          eventUri,
          {
            sdp: '',
          },
          this.empty
        );
      } else if (this.remoteSharePeerConn !== null) {
        this.sendChangeShareRequest(
          eventUri,
          {
            sdp: '',
          },
          this.empty
        );
      } else if (this.remoteSharePeerConn === null) {
        // this.remoteSharePeerConn = this.webRTCAdapter.getRTCPeerConnection(this.pluginIceServer);
        this.remoteSharePeerConn = new RTCPeerConnection(this.pluginIceServer);
        this.remoteSharePeerConn.onicecandidate = function (evt) {
          if (evt.candidate === null) {
            self.logInfo('RemoteSharePeerConn: Candidate collection complete');
          } else {
            self.sendCandidate(10, evt.candidate);
          }
        };
        this.remoteSharePeerConn.oniceconnectionstatechange = function (state) {
          self.logInfo(
            'remote share: oniceconnectionstatechange called ' +
              state.target.iceConnectionState
          );
          self.postLogToServer(
            'remote share: oniceconnectionstatechange called ' +
              state.target.iceConnectionState
          );
        };
        this.remoteSharePeerConn.onsignalingstatechange = function (state) {
          self.logInfo(
            'remote share: onsignalingstatechange called - ' +
              (state.target ? state.target.signalingState : state)
          );
          self.postLogToServer(
            'remote share: onsignalingstatechange called - ' +
              (state.target ? state.target.signalingState : state)
          );
        };
        const offerConstraints = {
          // offerToReceiveAudio: false,
          offerToReceiveAudio: true,
          offerToReceiveVideo: true,
        };
        this.remoteSharePeerConn.createOffer(
          function (offer) {
            self.logDebug('remote share: createOffer success ' + offer.sdp);
            self.remoteSharePeerOffer = offer;
            self.sendChangeShareRequest(eventUri, offer, function (s, e) {
              if (s) {
                if (self.remoteSharePeerOffer !== null) {
                  self.remoteSharePeerOffer = null;
                  self.remoteSharePeerConn.setLocalDescription(
                    offer,
                    function () {
                      self.logDebug(
                        'remote share: setlocaldescription success '
                      );
                    },
                    function (err) {
                      self.logError('remote share: SetLocalDescription error');
                    }
                  );
                } else {
                  self.logDebug('remote share: answer came first');
                }
              } else {
                self.logError('remote share: SetLocalDescription send error');
              }
            });
          },
          function (err) {
            self.logError('remote share: CreateOffer error');
          },
          offerConstraints
        );
      }
      if (newApp === 0) {
        const shareView = <HTMLVideoElement>(
          document.getElementById('shareVideo0')
        );
        this.webRTCAdapter.detachMediaStream(
          shareView,
          this.remoteShareStream,
          false,
          false
        );
        this.shareApp = newApp;
        const shareName = document.getElementById('shareName');
        shareName.innerHTML = '';
        this.remoteSharePeerConn.close();
        this.remoteSharePeerConn = null;
        this.remoteShareStream = null;
        this.remoteSharePeerOffer = null;
      }
    }

    sendConferenceInfoEvent() {
      const y = this.messages.event('OutEventConferenceInfoUpdate');
      this.outEventCallbackObject.dispatchOutEvent(y);
    }

    sendSdp(sdp: { sdp?: string }, callback: any) {
      if (this.callState === this.CallStateEnum.CONNECTED) {
        const params = <any>{};
        params.session = this.session;
        params.callId = this.callId;
        params.sdp = sdp;
        this.logInfo('main video sdp - ' + sdp.sdp);
        this.postMessageToServer('/setsdp', params, true, callback);
      }
    }

    muteLocalAudio(muted: boolean) {
      if (!this.localStream) {
        return;
      }
      const audioTrack = this.localStream.getAudioTracks()[0];
      let enabled = false;
      if (audioTrack) {
        enabled = this.localStream.getAudioTracks()[0].enabled;
      } else {
        return;
      }

      this.logDebug('MuteAudioIn - changing from ' + enabled + ' to ' + !muted);
      this.localStream.getAudioTracks()[0].enabled = !muted;
      const params = <any>{};
      params.session = this.session;
      params.callId = this.callId;
      params.muted = muted;
      this.postMessageToServer('/muteaudio', params, true, this.empty);
    }

    muteLocalVideo(muted: boolean) {
      const params = <any>{};
      params.session = this.session;
      params.callId = this.callId;
      params.muted = muted;
      this.videoMuted = muted;
      this.postMessageToServer('/mutevideo', params, true, this.empty);
    }

    handleMuteVideo(e: { willMute: boolean }) {
      this.muteLocalVideo(e.willMute);
    }

    handleFreezeImage(e: { freeze: any }) {
      const par = <any>{};
      par.callId = this.callId;
      par.session = this.session;
      par.enable = e.freeze;
      this.postMessageToServer('/freezeimage', par, true, this.empty);
      const elem = <HTMLVideoElement>document.getElementById('localVideo');
      if (elem) {
        if (e.freeze) {
          elem.pause();
        } else {
          elem.play();
        }
      }
    }

    handleMuteAudioIn(e: { willMute: boolean }) {
      this.muteLocalAudio(e.willMute);
    }

    handleMuteAudioOut(e: { willMute: boolean }) {
      for (let i = 0; i < this.maxSubscriptions; i++) {
        if (this.remoteStream[i]) {
          const audioTrack = this.remoteStream[i].getAudioTracks()[0];
          if (audioTrack) {
            const enabled = this.remoteStream[i].getAudioTracks()[0].enabled;
            this.logDebug(
              'AudioTrack' +
                i +
                ' : MuteAudioOut - changing from ' +
                enabled +
                ' to ' +
                !e.willMute
            );
            this.remoteStream[i].getAudioTracks()[0].enabled = !e.willMute;
          }
        }
      }
      this.audioOutMuted = e.willMute;
      const y = this.messages.event('OutEventMutedAudioOut');
      y.isMuted = e.willMute;
      this.outEventCallbackObject.dispatchOutEvent(y);
    }

    handleSetAudioVolume(volume: number) {
      this.volume = volume;
      for (let i = 0; i < this.maxSubscriptions; i++) {
        const remoteView = <HTMLVideoElement>(
          document.getElementById('remoteVideo' + i)
        );
        if (remoteView) {
          remoteView.volume = volume / 65000;
        }
      }
    }

    handleConnectResponse(response: {
      error: any;
      session?: any;
      callId?: any;
      secure?: any;
    }) {
      if (this.callState !== this.CallStateEnum.CONNECTING) {
        this.logError(
          'Invalid state for InitConnection response - ' + this.callState
        );
      }
      if (response.error !== 0) {
        const x = this.messages.event('callState');
        x.activeEid = -1;
        this.session = null;
        this.callId = -1;
        this.changeCallState(this.CallStateEnum.IDLE);
        x.error = 500;
        if (response.error === 4) {
          this.logInfo('ConnectResponse - CONFERENCE LOCKED !!');
          x.fault = 'ConferenceLocked';
          if (this.localStream) {
            this.webRTCAdapter.stopMediaStream(this.localStream);
          }
          this.sdpState = this.SDPStateEnum.IDLE;
        } else if (response.error === 6) {
          this.logInfo('ConnectResponse - WRONG PIN!!');
          x.fault = 'WrongPin';
          if (this.localStream) {
            this.webRTCAdapter.stopMediaStream(this.localStream);
          }
          this.sdpState = this.SDPStateEnum.IDLE;
        } else if (response.error === 35) {
          x.error = 6;
          x.type = 'OutEventLinked';
          if (this.localStream) {
            this.webRTCAdapter.stopMediaStream(this.localStream);
          }
          this.sdpState = this.SDPStateEnum.IDLE;
        } else if (response.error === 1017) {
          x.error = 500;
          x.type = 'OutEventLinked';
          if (this.localStream) {
            this.webRTCAdapter.stopMediaStream(this.localStream);
          }
          this.sdpState = this.SDPStateEnum.IDLE;
        } else {
          x.fault = response.error.toString();
          if (this.localStream) {
            this.webRTCAdapter.stopMediaStream(this.localStream);
          }
          this.sdpState = this.SDPStateEnum.IDLE;
        }
        this.outEventCallbackObject.dispatchOutEvent(x);
        return;
      }
      this.session = response.session;
      this.callId = response.callId;
      this.changeCallState(this.CallStateEnum.CONNECTED);
      this.collectIceAndSendSdp();
      if (this.sdpState === this.SDPStateEnum.COLLECTED) {
        this.sendSdp(null, null);
      }
      if (response.secure) {
        const signInEvent = this.messages.event('OutEventSignIn');
        signInEvent.signinSecured = true;
        this.outEventCallbackObject.dispatchOutEvent(signInEvent);
      }
    }

    handleDisconnectedEvent(localDisconnect: boolean) {
      const self = this;
      const x = this.messages.event('OutEventCallState');
      x.callState = 'Idle';
      x.error = 0;
      x.activeEid = -1;
      this.logInfo('CallState Idle --> UI');
      this.outEventCallbackObject.dispatchOutEvent(x);
      setTimeout(function () {
        const y = self.messages.event('OutEventConferenceEnded');
        self.outEventCallbackObject.dispatchOutEvent(y);
        self.logInfo('OutEventConferenceEnded --> UI');
        if (!localDisconnect) {
          const z = self.messages.event('OutEventUserMessage');
          z.messageType = 'DisconnectedFromConference';
          self.outEventCallbackObject.dispatchOutEvent(z);
        }
      }, 500);
    }

    handleParticipantsChanged(response: {
      names: any[];
      count: number;
      uris: any[];
    }) {
      const curentPartipantCount = this.participants.length;
      this.participants.length = 0;
      this.participantUris.length = 0;
      if (response.names) {
        for (let i = 0; i < response.count; i++) {
          // Recorder is counted as a participant, so need to make sure we don't try to get it's stream
          // Monitoring supervisors also don't send a stream, so exclude them from list of participants
          // Effectively, we want to ignore these participants
          if (
            response.names[i] !== 'Recorder' &&
            response.names[i].includes('-Monitor') === false
          ) {
            this.participants.push(response.names[i]);
            this.participantUris.push(response.uris[i]);
          }
        }
      }
      const participantNames = this.participants.toString();
      // Only send the particpants changed event if the particpant count has changed
      // This way, ignored participants don't generate the outEvent
      if (curentPartipantCount !== this.participants.length) {
        console.log(
          'Participants changed, current participants: ' + participantNames
        );
        const k = this.messages.event('OutEventParticipantsChanged');
        this.outEventCallbackObject.dispatchOutEvent(k);
        this.logInfo(
          'OutEventParticipantsChanged --> UI count=' + response.names.length
        );
      }

      if (this.currentSubscriptions === 0) {
        this.sendInConference();
      }
      this.updateSubscriptions();
    }

    handleRemoteSdp(sdp: RTCSessionDescriptionInit) {
      const self = this;
      const br = this.resolutionMap.hasOwnProperty(
        this.configurationRequest.videoPreferences
      )
        ? this.resolutionMap[this.configurationRequest.videoPreferences].br
        : '768';
      sdp.sdp = sdp.sdp.replace(
        /a=mid:video\r\n/g,
        'a=mid:video\r\nb=AS:' + br + '\r\n'
      );
      sdp.sdp = sdp.sdp.replace(
        /a=mid:sdparta_1\r\n/g,
        'a=mid:sdparta_1\r\nb=AS:' + br + '\r\n'
      );
      if (
        sdp.sdp.indexOf('m=video 1 RTP/SAVPF 96') >= 0 &&
        sdp.sdp.indexOf('a=rtpmap:96') === -1
      ) {
        console.log('fixing bad SDP received from server');
        sdp.sdp = sdp.sdp.replace(
          /a=rtpmap:98/g,
          // tslint:disable-next-line:max-line-length
          'a=rtpmap:96 VP8/90000\r\na=rtcp-fb:96 ccm fir\r\na=rtcp-fb:96 nack\r\na=rtcp-fb:96 nack pli\r\na=rtcp-fb:96 goog-remb\r\na=rtpmap:98'
        );
      }
      this.peerConn.setRemoteDescription(
        new RTCSessionDescription(sdp),
        function () {
          self.logInfo(
            'Remote SDP set successfully with type ' +
              sdp.type +
              ' sdp - ' +
              sdp.sdp
          );
        },
        function (e) {
          self.logError('Remote SDP set error, sdp: ' + sdp.sdp);
          console.error(e);
          console.dir(e);
          setTimeout(() => {
            this.handleRemoteSdp(sdp);
          }, 1500);
        }
      );
    }

    handleActiveTalkers(names: any[]) {
      this.activeTalkers.length = 0;
      for (let j = 0; j < names.length; j++) {
        this.activeTalkers.push(names[j]);
      }
      if (this.callState !== this.CallStateEnum.VIDEO_STARTED) {
        return;
      }
      for (let index = 0; index < names.length; index++) {
        const activetalker = names[index];
        console.log('Active talker: ' + activetalker.name);
      }
      const count = names.length;
      let active = 0;
      for (let i = 0; i < this.currentSubscriptions && i < count; i++) {
        const elemId = 'participant' + i;
        const docElement = document.getElementById(elemId);
        const videoElem = document.getElementById('remoteVideo' + i);
        if (names[i].active) {
          active += 1;
        }
        if (docElement && videoElem) {
          const participantName = docElement.innerHTML;
          const newName = names[i].name;
          const newNameArray = newName.split('-');
          let newDisplayName = names[i].name;
          // Remove the -Customer suffix when displaying name
          if (newName.indexOf('-Customer') !== -1) {
            newDisplayName = newName.substring(0, newName.indexOf('-Customer'));
          }
          // Remove the InteractionId when displaying name
          newNameArray.forEach((element: any[]) => {
            if (element.length === 10 && !isNaN(+element)) {
              newDisplayName = newNameArray[0];
            }
          });
          if (names[i].active) {
            if (participantName !== newDisplayName) {
              document.getElementById(elemId).innerHTML = newDisplayName;
              videoElem.style.visibility = 'visible';
              console.log('Participant' + i + 'name set to ' + newDisplayName);
            }
          } else {
            // docElement.innerHTML = "";
            // videoElem.style.visibility = "hidden";
          }
        }
      }
      if (active !== this.activeVideoStreams) {
        this.updateActiveVideoStreams(active);
      }
      const k = this.messages.event('OutEventActiveTalkersChanged');
      this.outEventCallbackObject.dispatchOutEvent(k);
    }

    handleShareAdded(response: {
      shares: {
        remoteAppUri: string[];
        remoteAppName: any[];
        numApp: string | number;
        currApp: any;
        eventUri: string;
        newApp: number;
      };
    }) {
      this.shareList.remoteAppUri = response.shares.remoteAppUri.slice(1);
      this.shareList.remoteAppName = response.shares.remoteAppName.slice(1);
      this.shareList.numApp = response.shares.numApp;
      this.shareList.currApp = response.shares.currApp;
      if (response.shares.eventUri === '') {
        response.shares.eventUri =
          response.shares.remoteAppUri[response.shares.numApp];
      }
      this.shareList.eventUri = response.shares.eventUri;
      this.shareList.newApp = response.shares.newApp;
      this.logInfo('Share added, shares are ' + JSON.stringify(this.shareList));
      const k = this.messages.event('OutEventAddShare');
      k.URI = response.shares.eventUri;
      this.outEventCallbackObject.dispatchOutEvent(k);
      this.logInfo('OutEventAddShare --> UI - ' + response.shares.eventUri);
    }

    handleShareRemoved(response: {
      shares: {
        remoteAppUri: string[];
        remoteAppName: any[];
        numApp: any;
        currApp: any;
        eventUri: string;
        newApp: number;
      };
    }) {
      this.shareList.remoteAppUri = response.shares.remoteAppUri.slice(1);
      this.shareList.remoteAppName = response.shares.remoteAppName.slice(1);
      this.shareList.numApp = response.shares.numApp;
      this.shareList.currApp = response.shares.currApp;
      this.shareList.eventUri = response.shares.eventUri;
      this.shareList.newApp = response.shares.newApp;
      this.logInfo(
        'Share removed, shares are - ' + JSON.stringify(this.shareList)
      );
      const k = this.messages.event('OutEventRemoveShare');
      k.URI = response.shares.eventUri;
      this.outEventCallbackObject.dispatchOutEvent(k);
      this.logInfo('OutEventRemoveShare --> UI - ' + response.shares.eventUri);
    }

    handleShareStreamChange(label: string, sdp: RTCSessionDescriptionInit) {
      const self = this;
      const shareName = document.getElementById('shareName');
      const dispName = this.shareList.remoteAppName[this.shareList.newApp - 1];
      const newNameArray = dispName.split('-');
      let newDisplayName = dispName;
      // Remove the -Customer suffix when displaying name
      if (dispName.indexOf('-Customer') !== -1) {
        newDisplayName = dispName.substring(0, dispName.indexOf('-Customer'));
      }
      // Remove the InteractionId when displaying name
      newNameArray.forEach((element) => {
        if (element.length === 10 && !isNaN(+element)) {
          newDisplayName = newNameArray[0];
          return;
        }
      });
      if (this.shareApp !== 0) {
        this.logInfo('Sharing going on, toggling, name - ' + newDisplayName);
        this.shareApp = this.shareList.newApp;
        shareName.innerHTML = newDisplayName;
        return;
      }
      if (this.shareList.newApp === 0) {
        this.logInfo('remote share: share stopped, returning');
        return;
      }
      if (!this.isBundleSupported()) {
        if (this.remoteSharePeerConn === null) {
          this.logInfo('remote share: share stopped, returning');
          return;
        }
        this.remoteSharePeerConn.onaddstream = function (evt) {
          self.logInfo('Remote Share: onaddstream called');
          self.remoteShareStream = evt.stream;
          self.remoteStream[self.remoteShareStreamIndex - 1] = evt.stream;
          self.shareApp = self.shareList.newApp;
          self.attachRemoteStream(
            label,
            self.remoteShareStreamIndex,
            'shareVideo0'
          );
        };
        const setRemoteShareRemoteDescription = function () {
          if (sdp.sdp && sdp.sdp.length > 0) {
            self.remoteSharePeerConn.setRemoteDescription(
              new RTCSessionDescription(sdp),
              function () {
                self.logInfo(
                  'remote share: Remote SDP set successfully with type ' +
                    sdp.type +
                    ' sdp - ' +
                    sdp.sdp
                );
              },
              function (e) {
                self.logError('remote share: Remote SDP set error');
                console.dir(e);
              }
            );
          }
        };
        if (this.remoteSharePeerOffer !== null) {
          this.logInfo('remote share: setLocalDescription first');
          const o = this.remoteSharePeerOffer;
          this.remoteSharePeerOffer = null;
          this.remoteSharePeerConn.setLocalDescription(
            o,
            function () {
              self.logDebug(
                'remote share: setlocaldescription success answer firstcase '
              );
              setRemoteShareRemoteDescription();
            },
            function (err) {
              self.logError('remote share: SetLocalDescription error');
            }
          );
        } else {
          setRemoteShareRemoteDescription();
        }
      } else {
        this.shareApp = this.shareList.newApp;
        this.attachRemoteStream(
          label,
          self.remoteShareStreamIndex,
          'shareVideo0'
        );
      }
      if (shareName) {
        shareName.innerHTML = newDisplayName;
        this.logInfo('Sharing setting name - ' + newDisplayName);
      }
    }

    handleStartLocalShareResponse(sdp: RTCSessionDescriptionInit) {
      const self = this;
      const setShareRemoteDescription = function () {
        self.localSharePeerConn.setRemoteDescription(
          new RTCSessionDescription(sdp),
          function () {
            self.logInfo(
              'Share: Remote SDP set successfully with type ' +
                sdp.type +
                ' sdp - ' +
                sdp.sdp
            );
          },
          function (e) {
            self.logError('Share: Remote SDP set error');
            console.dir(e);
          }
        );
      };
      if (this.localShareOffer !== null) {
        const o = this.localShareOffer;
        this.localShareOffer = null;
        this.logInfo('handleStartLocalShareResponse - setting offer first');
        this.localSharePeerConn.setLocalDescription(
          o,
          function () {
            setShareRemoteDescription();
          },
          function (err) {
            self.logError('Share: SetLocalDescription error');
          }
        );
      } else {
        setShareRemoteDescription();
      }
    }

    handleServerMutedAudio(muted: boolean, unmuteable: any) {
      if (unmuteable) {
        const k = this.messages.event('OutEventMutedAudioIn');
        k.isMuted = muted;
        this.outEventCallbackObject.dispatchOutEvent(k);
      } else {
        this.serverMutedAudio = muted;
        this.muteLocalAudio(muted);
        const y = this.messages.event('OutEventMutedServerAudioIn');
        y.isMuted = muted;
        this.outEventCallbackObject.dispatchOutEvent(y);
      }
    }

    handleServerMutedVideo(muted: boolean, unmuteable: any) {
      if (unmuteable) {
        const j = this.messages.event('OutEventMutedVideo');
        j.isMuted = muted;
        this.outEventCallbackObject.dispatchOutEvent(j);
      } else {
        this.serverMutedVideo = muted;
        this.muteLocalVideo(muted);
        const y = this.messages.event('OutEventMutedServerVideo');
        y.isMuted = muted;
        this.outEventCallbackObject.dispatchOutEvent(y);
      }
    }

    handleRecordingStatus(status: boolean) {
      if (status !== this.recording) {
        this.logInfo(
          'Recording changed from ' + this.recording + ' to ' + status
        );
        this.recording = status;
      }
      this.sendConferenceInfoEvent();
    }

    handleWebcastStatus(status: boolean) {
      if (status !== this.webcast) {
        this.logInfo('Webcast changed from ' + this.webcast + ' to ' + status);
        this.webcast = status;
      }
      this.sendConferenceInfoEvent();
    }

    handleGroupChatMessage(response: {
      uri: any;
      displayName: any;
      message: any;
    }) {
      const groupChatEvent = this.messages.event('OutEventGroupChat');
      groupChatEvent.uri = response.uri;
      groupChatEvent.displayName = response.displayName;
      groupChatEvent.message = response.message;
      this.outEventCallbackObject.dispatchOutEvent(groupChatEvent);
    }

    handlePrivateChatMessage(response: {
      uri: any;
      displayName: any;
      message: any;
    }) {
      const privateChatEvent = this.messages.event('OutEventPrivateChat');
      privateChatEvent.uri = response.uri;
      privateChatEvent.displayName = response.displayName;
      privateChatEvent.message = response.message;
      this.outEventCallbackObject.dispatchOutEvent(privateChatEvent);
    }

    handleLectureModeStage(stage: number) {
      let strStage = '';
      switch (stage) {
        case 0:
          strStage = 'LectureModeListen';
          this.lectureModeListener = true;
          this.updateSubscriptions();
          break;
        case 1:
          strStage = 'LectureModePresenter';
          this.lectureModeListener = false;
          this.updateSubscriptions();
          break;
        case 2:
          strStage = 'LectureModeAllowedToSpeak';
          break;
        case 3:
          strStage = 'LectureModeStarted';
          this.logInfo('Lecture Mode Started');
          this.lectureModeStarted = true;
          break;
        case 10:
          strStage = 'LectureModeStopped';
          this.logInfo('Lecture Mode Stopped');
          this.lectureModeListener = false;
          this.lectureModeStarted = false;
          this.updateSubscriptions();
          break;
        default:
          this.logError('Unknown LectureModeStage - ' + stage);
          break;
      }
      const lectureModeStageEvt = this.messages.event(
        'OutEventLectureModeStage'
      );
      lectureModeStageEvt.stage = strStage;
      this.outEventCallbackObject.dispatchOutEvent(lectureModeStageEvt);
    }

    handleLectureHand() {
      const lectureHandClearedEvt = this.messages.event(
        'OutEventLectureModeHandCleared'
      );
      this.outEventCallbackObject.dispatchOutEvent(lectureHandClearedEvt);
    }

    handleLectureModePresenterChanged(uriPresenter: string) {
      this.lectureModePresenterUri = uriPresenter;
      const lectureModePresenterChanged = this.messages.event(
        'OutEventLectureModePresenterChanged'
      );
      lectureModePresenterChanged.uriOfPresenter = uriPresenter;
      this.outEventCallbackObject.dispatchOutEvent(lectureModePresenterChanged);
    }

    handleCandidate(candidate: {
      candidate: any;
      sdpMid: any;
      sdpMlineIndex: any;
      streamId: number;
    }) {
      const o = {
        candidate: candidate.candidate,
        sdpMid: candidate.sdpMid,
        sdpMLineIndex: candidate.sdpMlineIndex,
      };
      const c = new RTCIceCandidate(o);
      switch (candidate.streamId) {
        case 0:
          this.localSharePeerConn.addIceCandidate(c);
          break;
        case 1:
          this.peerConn.addIceCandidate(c);
          break;
        case 10:
          this.remoteSharePeerConn.addIceCandidate(c);
          break;
        default:
          if (this.additionalPeerConn[candidate.streamId - 1]) {
            this.additionalPeerConn[candidate.streamId - 1].addIceCandidate(c);
          }
          break;
      }
    }

    localDescCreated(description: RTCSessionDescriptionInit) {
      const self = this;
      this.sendSdp(description, function (s: any, e: any) {
        if (s) {
          self.peerConn.setLocalDescription(
            description,
            function () {
              self.logDebug(
                `LocalDescCreated: Changing SDP State to COLLECTED from ${self.sdpState}`
              );
              self.sdpState = self.SDPStateEnum.COLLECTED;
              if (self.callState === self.CallStateEnum.IDLE) {
                self.webRTCAdapter.stopMediaStream(self.localStream);
                self.sdpState = self.SDPStateEnum.IDLE;
              } else {
              }
            },
            function (err) {
              self.logError('SetLocalDescription error');
              console.dir(err);
            }
          );
        } else {
          self.webRTCAdapter.stopMediaStream(self.localStream);
          self.sdpState = self.SDPStateEnum.IDLE;
          self.logError('SetLocalDescription send error');
        }
      });
    }

    postMessageToServer(
      url: string,
      params: any,
      doLog: boolean,
      callback: any
    ) {
      const oReq = new XMLHttpRequest();
      const self = this;
      oReq.open('post', this.webRTCServer + url, true);
      oReq.onload = function () {
        if (doLog) {
          self.logDebug(
            '' +
              url +
              ' response received with status ' +
              oReq.status +
              ' : ' +
              oReq.statusText
          );
        }
        callback(true, oReq.status);
      };
      oReq.onerror = function (e) {
        self.logError('' + url + ' error response');
        console.dir(e);
        callback(false, e);
      };
      const paramStr = JSON.stringify(params);
      if (doLog) {
        self.logInfo('' + url + ' - ' + paramStr);
      }
      oReq.send(paramStr);
    }

    postLogToServer(log: string) {
      const params = <any>{};
      params.session = this.session;
      params.callId = this.callId;
      params.log = log;
      this.postMessageToServer('/log', params, false, this.empty);
    }

    updateSelfView(val: any) {
      this.logDebug('UpdateSelfView - ' + val);
    }

    updateActiveVideoStreams(active: number) {
      this.logInfo(
        'OutEventVideoStreamsChanged --> UI streamCount=' +
          active +
          ' prev=' +
          this.activeVideoStreams
      );
      this.activeVideoStreams = active;
      const streamsChangedEvent = this.messages.event(
        'OutEventVideoStreamsChanged'
      );
      streamsChangedEvent.streamCount = active;
      this.outEventCallbackObject.dispatchOutEvent(streamsChangedEvent);
      this.windowSizes = this.getWindowSizes();
      this.windowSizeChanged(this.windowSizes);
    }

    updateSubscriptions() {
      let count = this.participants.length;
      this.logInfo(
        'Updating subscriptions, current participant count: ' + count
      );
      if (count <= 0) {
        return;
      }
      if (this.lectureModeListener) {
        count = 1;
      }
      if (count > 1) {
        count -= 1;
      }
      if (count > this.maxSubscriptions) {
        count = this.maxSubscriptions;
      }
      if (this.currentSubscriptions !== count) {
        if (this.currentSubscriptions < count) {
          while (this.currentSubscriptions < count) {
            this.logInfo(
              'Count increased to ' +
                count +
                ' adding subscription to ' +
                this.currentSubscriptions +
                ' Max is ' +
                this.maxSubscriptions
            );
            if (this.currentSubscriptions < this.maxSubscriptions) {
              this.addSubscription();
            }
          }
        } else {
          while (this.currentSubscriptions > count) {
            this.logInfo(
              'Count decreased to ' +
                count +
                ' removing subscription from ' +
                this.currentSubscriptions
            );
            this.removeSubscription();
            if (this.activeVideoStreams > this.currentSubscriptions) {
              this.updateActiveVideoStreams(this.currentSubscriptions);
            }
          }
        }
      }
    }

    updateRemoteShareStats() {
      const self = this;
      if (this.shareApp > 0) {
        if (!this.localStats.remoteShare) {
          this.localStats.remoteShare = {
            videoResolution: {
              height: 0,
              width: 0,
            },
            videoFrameRate: 0,
            videoDecodedFrameRate: 0,
            videoDisplayedFrameRate: 0,
            videoKBitsPerSecRecv: 0,
            firs: 0,
            nacks: 0,
            packetsLost: 0,
            lastBytesReceivedTS: Date.now(),
            noStats: true,
          };
        }
        const track = this.remoteStream[
          this.remoteShareStreamIndex - 1
        ].getVideoTracks()[0];
        let pc = this.peerConn;
        if (!this.isBundleSupported()) {
          pc = this.remoteSharePeerConn;
        }
        this.webRTCAdapter.getWebRTCStats(pc, track, function (streamStats) {
          if (!streamStats.hasOwnProperty('bytesReceived')) {
            return;
          }
          if (self.localStats.remoteShare.bytesReceived) {
            const timeDiff =
              (Date.now() - self.localStats.remoteShare.lastBytesReceivedTS) /
              1000;
            const bitDiff =
              ((streamStats.bytesReceived -
                self.localStats.remoteShare.bytesReceived) *
                8) /
              1000;
            streamStats.videoKBitsPerSecRecv = Math.floor(bitDiff / timeDiff);
          } else {
            streamStats.videoKBitsPerSecRecv = 0;
            streamStats.noStats = true;
          }
          self.localStats.remoteShare = streamStats;
          self.localStats.remoteShare.lastBytesReceivedTS = Date.now();
        });
      }
    }

    updateEncodeStats() {
      if (!this.localStats.local) {
        this.localStats.local = {
          videoResolution: {
            height: 0,
            width: 0,
          },
          captureFrameRate: 0,
          encodeFrameRate: 0,
          videoKBitsPerSecSend: 0,
          numFirs: 0,
          numNacks: 0,
          lastBytesSentTS: Date.now(),
          noStats: true,
        };
      }
      const self = this;
      let track = this.localStream.getVideoTracks()[0];
      this.webRTCAdapter.getWebRTCStats(self.peerConn, track, function (stats) {
        if (!stats.hasOwnProperty('bytesSent')) {
          return;
        }
        if (self.localStats.local.bytesSent) {
          const timeDiff =
            (Date.now() - self.localStats.local.lastBytesSentTS) / 1000;
          const bitDiff =
            ((stats.bytesSent - self.localStats.local.bytesSent) * 8) / 1000;
          stats.videoKBitsPerSecSend = Math.floor(bitDiff / timeDiff);
        } else {
          stats.videoKBitsPerSecSend = 0;
          stats.noStats = true;
        }
        self.localStats.local = stats;
        self.localStats.local.lastBytesSentTS = Date.now();
      });
      if (this.localShareStream !== null) {
        if (!this.localStats.localShare) {
          this.localStats.localShare = {
            videoResolution: {
              height: 0,
              width: 0,
            },
            captureFrameRate: 0,
            encodeFrameRate: 0,
            videoKBitsPerSecSend: 0,
            numFirs: 0,
            numNacks: 0,
            lastBytesSentTS: Date.now(),
            noStats: true,
          };
        }
        track = self.localShareStream.getVideoTracks()[0];
        this.webRTCAdapter.getWebRTCStats(
          self.localSharePeerConn,
          track,
          function (stats) {
            if (!stats.hasOwnProperty('bytesSent')) {
              return;
            }
            if (self.localStats.localShare.bytesSent) {
              const timeDiff =
                (Date.now() - self.localStats.localShare.lastBytesSentTS) /
                1000;
              const bitDiff =
                ((stats.bytesSent - self.localStats.localShare.bytesSent) * 8) /
                1000;
              stats.videoKBitsPerSecSend = Math.floor(bitDiff / timeDiff);
            } else {
              stats.videoKBitsPerSecSend = 0;
              stats.noStats = true;
            }
            self.localStats.localShare = stats;
            self.localStats.localShare.lastBytesSentTS = Date.now();
          }
        );
      }
    }

    watchForViewChange() {
      const self = this;
      this.watchInterval = setInterval(function () {
        const windowSizes = self.getWindowSizes();
        if (windowSizes.length !== self.windowSizes.length) {
          self.windowSizes = windowSizes;
          self.windowSizeChanged(windowSizes);
        } else {
          for (let i = 0; i < windowSizes.length; i++) {
            if (
              windowSizes[i].height !== self.windowSizes[i].height ||
              windowSizes[i].width !== self.windowSizes[i].width
            ) {
              self.windowSizes = windowSizes;
              self.windowSizeChanged(windowSizes);
              break;
            }
          }
        }
        self.updateEncodeStats();
        for (let k = 0; k < self.activeVideoStreams; k++) {
          const stats = {};
          self.getStatsForStreamIndex(k, stats);
        }
        if (self.shareApp > 0) {
          self.updateRemoteShareStats();
        }
        self.displayStats();
      }, 5000);
    }

    windowSizeChanged(windowSizes: any[]) {
      if (windowSizes.length <= 0) {
        return;
      }
      const reqObj = <any>{};
      reqObj.windowSizes = windowSizes;
      reqObj.numPreferred = 0;
      if (
        (this.shareList.newApp === 0 && this.activeVideoStreams > 2) ||
        (this.numPreferred === 1 && this.activeVideoStreams > 2)
      ) {
        reqObj.numPreferred = 1;
      }
      const params = <any>{};
      params.session = this.session;
      params.callId = this.callId;
      params.windows = reqObj;
      this.postMessageToServer('/windowsizes', params, true, this.empty);
    }

    webRTCGetSources(audioSources: any[], videoSources: any[]) {
      this.audioSources = audioSources;
      this.videoSources = videoSources;
      this.configurationRequest.cameras = [];
      this.configurationRequest.microphones = [];
      this.configurationRequest.speakers = [];
      for (let i = 0; i < this.videoSources.length; i++) {
        this.configurationRequest.cameras[i] = this.videoSources[i].label;
        this.logInfo('Source Camera: ' + this.videoSources[i].label);
      }
      this.configurationRequest.numberCameras = this.videoSources.length;
      this.configurationRequest.currentCamera = 0;
      for (let j = 0; j < this.audioSources.length; j++) {
        this.configurationRequest.microphones[j] = this.audioSources[j].label;
        this.logInfo('Source Mic: ' + this.audioSources[j].label);
      }
      this.configurationRequest.numberMicrophones = this.audioSources.length;
      this.configurationRequest.currentMicrophone = 0;
      this.configurationRequest.speakers[0] = 'Default';
      this.configurationRequest.numberSpeakers = 1;
      this.configurationRequest.currentSpeaker = 0;
      const devicesEvt = this.messages.event('OutEventDevicesChanged');
      this.outEventCallbackObject.dispatchOutEvent(devicesEvt);
      this.logInfo('OutEventPluginConnectionSuccess --> UI ');
      const evt = this.messages.event('OutEventPluginConnectionSuccess');
      this.outEventCallbackObject.dispatchOutEvent(evt);
    }
  }
}
